import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, UserPlus, Loader2, Copy, Check, Trash2, Ban, RotateCcw,
  Activity, Mail, ShieldCheck, ClipboardList, BellRing, History,
  MessageSquare, Star, Bug, Lightbulb, Heart, MoreHorizontal, CheckCircle2, RefreshCw, Music2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Legend } from "recharts";
import { TourShotsPanel } from "@/components/TourShotsPanel";
import { DemoAnalyticsPanel } from "@/components/DemoAnalyticsPanel";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtWhen = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never");
const isActiveRecent = (s) => s && (Date.now() - new Date(s).getTime()) < 7 * 864e5;

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700",
  invited: "bg-amber-100 text-amber-700",
  disabled: "bg-red-100 text-red-700",
};

const DELIVERY_META = {
  delivered: { label: "email delivered", cls: "bg-emerald-100 text-emerald-700", hint: "The invite email reached their inbox provider" },
  opened: { label: "email opened", cls: "bg-emerald-100 text-emerald-700", hint: "They opened the invite email" },
  clicked: { label: "link clicked", cls: "bg-emerald-100 text-emerald-700", hint: "They clicked the invite link" },
  sent: { label: "email sent", cls: "bg-sky-100 text-sky-700", hint: "Sent — awaiting delivery confirmation" },
  queued: { label: "email queued", cls: "bg-sky-100 text-sky-700", hint: "Queued at the email provider" },
  delivery_delayed: { label: "delivery delayed", cls: "bg-amber-100 text-amber-700", hint: "The recipient's server is delaying delivery — will retry" },
  bounced: { label: "email bounced", cls: "bg-red-100 text-red-700", hint: "The address rejected the email — double-check the spelling" },
  suppressed: { label: "email blocked", cls: "bg-red-100 text-red-700", hint: "This address previously bounced and is suppressed — verify the address, then re-invite" },
  complained: { label: "marked spam", cls: "bg-red-100 text-red-700", hint: "The recipient marked a previous email as spam" },
};

function DeliveryChip({ info }) {
  if (!info) return null;
  const meta = DELIVERY_META[info.last_event];
  return (
    <span className="flex flex-wrap gap-1 mt-1">
      {meta && (
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.cls}`} title={meta.hint}>{meta.label}</span>
      )}
      {info.link_expired && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600" title="The 7-day invite link expired — resend or copy a fresh link">link expired</span>
      )}
    </span>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [activity, setActivity] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [delivery, setDelivery] = useState({});
  const [roleHist, setRoleHist] = useState(null);
  const [roleHistOpen, setRoleHistOpen] = useState(false);
  const [onboardEnabled, setOnboardEnabled] = useState(null);

  useEffect(() => {
    api.get("/settings/onboarding").then(({ data }) => setOnboardEnabled(!!data.enabled)).catch(() => setOnboardEnabled(true));
  }, []);

  const toggleOnboarding = async () => {
    const next = !onboardEnabled;
    setOnboardEnabled(next);
    try {
      await api.put("/admin/settings/onboarding", { enabled: next });
      toast.success(next ? "Maestro onboarding is ON — new users get the guided intro" : "Maestro onboarding is OFF — new users go straight to the dashboard");
    } catch (e) {
      setOnboardEnabled(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update the setting");
    }
  };

  const openRoleHistory = async () => {
    setRoleHistOpen(true); setRoleHist(null);
    try { const { data } = await api.get("/admin/role-changes"); setRoleHist(data); }
    catch (e) { setRoleHist([]); }
  };

  const setPlan = async (u, plan) => {
    try {
      await api.put(`/admin/users/${u.id}/plan`, { plan: plan === "trial" ? "" : plan });
      toast.success(plan === "trial" ? `${u.name || u.email} back on trial` : `${u.name || u.email} set to ${plan}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update plan"); }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role === "pm" ? "a Beta tester (PM)" : role === "supervisor" ? "a Supervisor" : "an Admin"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/admin/users").then((r) => r.data).catch(() => []),
      api.get("/admin/feedback").then((r) => r.data).catch(() => []),
    ]).then(([s, u, f]) => { setStats(s); setUsers(u); setFeedback(f || []); setLoading(false); });
    api.get("/admin/invite-delivery").then((r) => setDelivery(r.data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openActivity = async (u) => {
    setActivityUser(u); setActivity(null);
    try { const { data } = await api.get(`/admin/users/${u.id}/activity`); setActivity(data); }
    catch (e) { setActivity([]); }
  };

  const setStatus = async (u, status) => {
    try { await api.patch(`/admin/users/${u.id}/status`, { status }); toast.success(status === "disabled" ? "User deactivated" : "User activated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their account.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
      const url = `${window.location.origin}/accept-invite/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(data.email_sent ? "Invite email re-sent" : "New invite link copied to clipboard");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copyLink = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/invite-link`);
      await navigator.clipboard.writeText(data.invite_url).catch(() => {});
      toast.success("Fresh invite link copied — valid for 7 days (no email sent)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const nudgeAll = async () => {
    try {
      const { data } = await api.post("/admin/nudge-inactive?days=7");
      if (data.targeted === 0) toast.info("No inactive testers to nudge 🎉");
      else if (data.emailed === data.targeted) toast.success(`Nudged ${data.emailed}/${data.targeted} inactive tester(s) by email`);
      else if (data.emailed > 0) toast.warning(`Nudged ${data.emailed}/${data.targeted}. Some failed: ${data.error || "check sender/domain config"}`);
      else toast.error(data.error || "Could not send nudge emails");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const nudgeOne = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/nudge`);
      if (data.emailed) toast.success(`Nudge emailed to ${u.email}`);
      else toast.error(data.error || "Could not send the nudge email");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="overline text-muted-foreground mb-2">Beta program</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3"><Users className="h-9 w-9 text-primary" /> Beta Testers</h1>
          <p className="text-muted-foreground mt-2">Invite testers, track their usage, and manage access.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button type="button" onClick={toggleOnboarding} disabled={onboardEnabled === null}
            className={`h-11 inline-flex items-center gap-2 rounded-md border px-4 text-sm font-semibold transition-colors ${onboardEnabled ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-border bg-secondary text-muted-foreground hover:bg-secondary/70"}`}
            title="Controls whether new users see the guided Meet Maestro intro on their first visit"
            data-testid="admin-onboarding-toggle">
            <Music2 className="h-4 w-4" />
            Onboarding: {onboardEnabled === null ? "…" : onboardEnabled ? "On" : "Off"}
            <span className={`ml-1 h-4 w-7 rounded-full relative transition-colors ${onboardEnabled ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all ${onboardEnabled ? "left-3.5" : "left-0.5"}`} />
            </span>
          </button>
          <Button variant="outline" className="h-11 gap-2" onClick={openRoleHistory} data-testid="admin-role-history-button"><History className="h-4 w-4" /> Role history</Button>
          <Button variant="outline" className="h-11 gap-2" onClick={nudgeAll} data-testid="admin-nudge-all-button"><BellRing className="h-4 w-4" /> Nudge inactive</Button>
          <Button className="h-11 gap-2" onClick={() => setInviteOpen(true)} data-testid="admin-invite-button"><UserPlus className="h-4 w-4" /> Invite tester</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="admin-stats">
        <Stat label="Testers" value={stats?.total_users} />
        <Stat label="Active (7d)" value={stats?.active_7d} color="#10B981" />
        <Stat label="Inactive (7d)" value={stats?.inactive_7d} color="#F59E0B" />
        <Stat label="Invited" value={stats?.invited} color="#0044FF" />
        <Stat label="Projects" value={stats?.projects} />
        <Stat label="AI runs" value={stats?.ai_generations} color="#7C3AED" />
        <Stat label="Feedback" value={stats?.feedback} color="#EF4444" />
      </div>
      {/* NDA compliance */}
      <NdaCompliance users={users} />
      <OnboardingInsights users={users} />
      <TourShotsPanel />
      <DemoAnalyticsPanel />
      {/* Users table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-secondary/50 text-left text-muted-foreground">
              <tr>
                {["User", "Status", "Role", "NDA", "Terms", "Signed up", "Last active", "Logins", "Projects", "Tasks", "AI", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-muted-foreground">No testers yet — invite your first one.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-t border-border hover:bg-secondary/30" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">{u.name}
                      {u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-primary" title="Admin" />}
                      {u.role === "supervisor" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid={`role-badge-${u.id}`}>SUPERVISOR</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                    {(u.role_profile || u.industry) && (
                      <div className="text-[10px] text-[#2f6bff] font-semibold mt-0.5" data-testid={`onboarding-answers-${u.id}`}>
                        {[u.role_profile, u.industry, u.plan_type ? `${u.plan_type} plan` : null, u.autopilot_optin === true ? "Auto Pilot ✓" : null].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[u.status] || "bg-secondary text-foreground"}`}>{u.status}</span>
                    {u.status === "active" && <span className={`ml-1 text-[10px] ${isActiveRecent(u.last_active) ? "text-emerald-600" : "text-muted-foreground"}`}>{isActiveRecent(u.last_active) ? "● live" : ""}</span>}
                    {u.status === "invited" && <span data-testid={`invite-delivery-${u.id}`}><DeliveryChip info={delivery[u.id]} /></span>}
                  </td>
                  <td className="px-4 py-3" data-testid={`admin-role-cell-${u.id}`}>
                    {u.id === user.id ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-primary/10 text-primary whitespace-nowrap" title="That's you — you can't change your own role"><ShieldCheck className="h-3 w-3" /> Admin (you)</span>
                    ) : (
                      <Select value={u.role || "pm"} onValueChange={(r) => setRole(u, r)}>
                        <SelectTrigger className="h-8 w-[132px] text-xs" data-testid={`admin-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pm">Beta tester (PM)</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {u.role === "pm" && u.id !== user.id && (
                      <Select value={u.plan || "trial"} onValueChange={(p) => setPlan(u, p)}>
                        <SelectTrigger className="h-8 w-[132px] text-xs mt-1.5" data-testid={`admin-plan-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trial">Trial (no plan)</SelectItem>
                          <SelectItem value="team">Team</SelectItem>
                          <SelectItem value="growth">Growth</SelectItem>
                          <SelectItem value="command">Command</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`admin-nda-${u.id}`}>
                    {u.nda_signed ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                        title={`Signed ${fmtDate(u.nda_signed_at)}${u.nda_signature ? ` by ${u.nda_signature}` : ""}${u.nda_version ? `\n${u.nda_version}` : ""}`}>
                        <ShieldCheck className="h-3 w-3" /> {fmtDate(u.nda_signed_at)}
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground" title="Registered before the NDA requirement or invite not yet accepted">not signed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`admin-terms-${u.id}`}>
                    {u.terms_accepted ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700"
                        title={`Agreed to Terms & Privacy Policy ${fmtDate(u.terms_accepted_at)}${u.terms_version ? `\nVersion ${u.terms_version}` : ""}`}>
                        <CheckCircle2 className="h-3 w-3" /> {fmtDate(u.terms_accepted_at)}
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground" title="Registered before the Terms consent requirement or invite not yet accepted">not captured</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtWhen(u.last_active)}</td>
                  <td className="px-4 py-3">{u.login_count || 0}</td>
                  <td className="px-4 py-3">{u.projects}</td>
                  <td className="px-4 py-3">{u.tasks}</td>
                  <td className="px-4 py-3">{u.ai_generations || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <IconBtn title="Activity" onClick={() => openActivity(u)} testid={`admin-activity-${u.id}`}><Activity className="h-4 w-4" /></IconBtn>
                      {u.role === "supervisor" && (
                        <IconBtn title="Assign PMs" onClick={() => setAssignFor(u)} testid={`admin-assign-pms-${u.id}`}><Users className="h-4 w-4 text-violet-600" /></IconBtn>
                      )}
                      {u.status === "active" && u.role !== "admin" && !isActiveRecent(u.last_active) && (
                        <IconBtn title="Nudge (re-engage)" onClick={() => nudgeOne(u)} testid={`admin-nudge-${u.id}`}><BellRing className="h-4 w-4 text-amber-600" /></IconBtn>
                      )}
                      {u.status === "invited" && <IconBtn title="Copy invite link (no email)" onClick={() => copyLink(u)} testid={`admin-copy-link-${u.id}`}><Copy className="h-4 w-4" /></IconBtn>}
                      {u.status === "invited" && <IconBtn title="Resend invite" onClick={() => resend(u)} testid={`admin-resend-${u.id}`}><Mail className="h-4 w-4" /></IconBtn>}
                      {u.role !== "admin" && (u.status === "disabled"
                        ? <IconBtn title="Activate" onClick={() => setStatus(u, "active")} testid={`admin-activate-${u.id}`}><RotateCcw className="h-4 w-4 text-emerald-600" /></IconBtn>
                        : <IconBtn title="Deactivate" onClick={() => setStatus(u, "disabled")} testid={`admin-deactivate-${u.id}`}><Ban className="h-4 w-4 text-amber-600" /></IconBtn>)}
                      {u.role !== "admin" && <IconBtn title="Delete" onClick={() => del(u)} testid={`admin-delete-${u.id}`}><Trash2 className="h-4 w-4 text-red-500" /></IconBtn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FeedbackPanel feedback={feedback} loading={loading} onReload={load} />

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onDone={load} />

      <AssignPmsDialog supervisor={assignFor} users={users} onOpenChange={(v) => !v && setAssignFor(null)} onDone={load} />

      <Dialog open={roleHistOpen} onOpenChange={setRoleHistOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="admin-role-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Role change history</DialogTitle>
            <DialogDescription>Who changed whose permissions, and when.</DialogDescription>
          </DialogHeader>
          {roleHist === null ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : roleHist.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="role-history-empty">No role changes recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {roleHist.map((e, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm border-b border-border/50 pb-2" data-testid={`role-history-row-${i}`}>
                  <span className="min-w-0"><b>{e.detail}</b><span className="block text-xs text-muted-foreground">by {e.actor_name} ({e.actor_email})</span></span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">{fmtWhen(e.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityUser} onOpenChange={(o) => !o && setActivityUser(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /> Activity — {activityUser?.name}</DialogTitle>
            <DialogDescription>{activityUser?.email}</DialogDescription>
          </DialogHeader>
          {activity === null ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No recorded activity yet.</p>
          ) : (
            <ul className="space-y-2">
              {activity.map((a, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-border/50 pb-2">
                  <span><b className="capitalize">{a.action.replace(/_/g, " ")}</b>{a.detail ? <span className="text-muted-foreground"> — {a.detail}</span> : ""}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtWhen(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function InviteDialog({ open, onOpenChange, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("pm");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => { setEmail(""); setName(""); setRole("pm"); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/users/invite", { email, name, role });
      const url = `${window.location.origin}/accept-invite/${data.token}`;
      setResult({ ...data, url });
      await navigator.clipboard.writeText(url).catch(() => {});
      if (data.email_sent) toast.success("Invite emailed & link copied");
      else if (data.email_error) toast.warning("Link copied — email couldn't be sent (see note)");
      else toast.success("Invite link copied to clipboard");
      onDone?.();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not invite");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="admin-invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Invite a beta tester</DialogTitle>
          <DialogDescription>We'll generate a secure invite link (and email it if email is configured). The tester sets their own password.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm">Invite created for <b>{result.email}</b>.</p>
            <div className="border border-border rounded-lg p-3 bg-secondary/30">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Invite link {result.email_sent ? "(also emailed)" : "(share this manually)"}</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={result.url} className="text-xs h-9" data-testid="invite-result-url" onFocus={(e) => e.target.select()} />
                <Button size="icon" variant="ghost" className="shrink-0 h-9 w-9" onClick={async () => { await navigator.clipboard.writeText(result.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="invite-copy-button">
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {!result.email_sent && result.email_error && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="invite-email-error">Email not delivered: {result.email_error} — share the link above manually for now.</p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={reset} data-testid="invite-another-button">Invite another</Button>
              <Button onClick={() => onOpenChange(false)} data-testid="invite-done-button">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tester@company.com" className="mt-1.5" data-testid="invite-email-input" />
            </div>
            <div>
              <Label htmlFor="inv-name">Name (optional)</Label>
              <Input id="inv-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="invite-name-field" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pm">Beta tester (PM)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading || !email.trim()} className="gap-2" data-testid="invite-send-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Create invite</>}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FeedbackPanel({ feedback, loading, onReload }) {
  const [filter, setFilter] = useState("all");
  const CAT = {
    bug: { label: "Bug", Icon: Bug, color: "#EF4444" },
    idea: { label: "Idea", Icon: Lightbulb, color: "#0044FF" },
    praise: { label: "Praise", Icon: Heart, color: "#10B981" },
    other: { label: "Other", Icon: MoreHorizontal, color: "#7C3AED" },
  };
  const list = feedback.filter((f) => filter === "all" || (filter === "open" ? f.status === "open" : filter === "resolved" ? f.status === "resolved" : f.category === filter));

  const setStatus = async (f, status) => {
    try { await api.patch(`/admin/feedback/${f.id}`, { status }); toast.success(status === "resolved" ? "Marked resolved" : "Reopened"); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (f) => {
    if (!window.confirm("Delete this feedback?")) return;
    try { await api.delete(`/admin/feedback/${f.id}`); toast.success("Feedback deleted"); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filters = ["all", "open", "resolved", "bug", "idea", "praise", "other"];

  return (
    <div className="mt-10" data-testid="admin-feedback">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <h2 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><MessageSquare className="h-6 w-6 text-primary" /> Feedback <span className="text-base font-medium text-muted-foreground">({feedback.length})</span></h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          {filters.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`feedback-filter-${f}`}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-feedback-table">
            <thead className="bg-secondary/50 text-left text-muted-foreground">
              <tr>{["Type", "Rating", "Page", "Message", "From", "When", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No feedback yet.</td></tr>
              ) : list.map((f) => {
                const c = CAT[f.category] || CAT.other;
                return (
                  <tr key={f.id} className={`border-t border-border hover:bg-secondary/30 ${f.status === "resolved" ? "opacity-60" : ""}`} data-testid={`feedback-row-${f.id}`}>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: c.color }}><c.Icon className="h-3.5 w-3.5" /> {c.label}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap">{f.rating ? <span className="inline-flex items-center gap-0.5 text-amber-500 font-semibold"><Star className="h-3.5 w-3.5" fill="#F59E0B" /> {f.rating}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><div className="font-medium">{f.page_label || "—"}</div><div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{f.page}</div></td>
                    <td className="px-4 py-3 max-w-[320px]"><p className="whitespace-pre-wrap break-words">{f.message || <span className="text-muted-foreground italic">(no comment)</span>}</p></td>
                    <td className="px-4 py-3"><div className="font-medium">{f.user_name}</div><div className="text-[11px] text-muted-foreground">{f.user_email || "anonymous"}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtWhen(f.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {f.status === "resolved"
                          ? <IconBtn title="Reopen" onClick={() => setStatus(f, "open")} testid={`feedback-reopen-${f.id}`}><RefreshCw className="h-4 w-4 text-amber-600" /></IconBtn>
                          : <IconBtn title="Mark resolved" onClick={() => setStatus(f, "resolved")} testid={`feedback-resolve-${f.id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></IconBtn>}
                        <IconBtn title="Delete" onClick={() => del(f)} testid={`feedback-delete-${f.id}`}><Trash2 className="h-4 w-4 text-red-500" /></IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-2xl font-extrabold mt-1" style={color ? { color } : {}}>{value ?? "—"}</p>
    </div>
  );
}

function IconBtn({ children, title, onClick, testid }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testid} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-secondary transition-colors">
      {children}
    </button>
  );
}

function AssignPmsDialog({ supervisor, users, onOpenChange, onDone }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (supervisor) setSelected(supervisor.assigned_pm_ids || []); }, [supervisor]);
  const candidates = users.filter((u) => supervisor && u.id !== supervisor.id && u.role !== "admin");
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${supervisor.id}/assigned-pms`, { pm_ids: selected });
      toast.success(selected.length ? `Assigned ${selected.length} PM(s)` : "Cleared — supervisor now sees ALL PMs");
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!supervisor} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assign-pms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-violet-600" /> Assign PMs — {supervisor?.name}</DialogTitle>
          <DialogDescription>Leave empty to let this supervisor audit ALL PMs. Select specific PMs to limit their view.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No other users to assign.</p>
          ) : candidates.map((u) => (
            <label key={u.id} className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-secondary/40 cursor-pointer">
              <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`assign-pm-${u.id}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="assign-pms-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save assignment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TREND_COLORS = ["#2f6bff", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#64748b"];

function IndustryTrend({ answered }) {
  const dated = answered.filter((u) => u.industry && (u.onboarding_updated_at || u.created_at));
  if (dated.length < 1) return null;
  const industries = [...new Set(dated.map((u) => u.industry))];
  const top = industries.slice(0, 5);
  const buckets = {};
  dated.forEach((u) => {
    const d = new Date(u.onboarding_updated_at || u.created_at);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const ind = top.includes(u.industry) ? u.industry : "Other";
    buckets[k] = buckets[k] || {};
    buckets[k][ind] = (buckets[k][ind] || 0) + 1;
  });
  const months = Object.keys(buckets).sort().slice(-6);
  const data = months.map((k) => {
    const [y, m] = k.split("-");
    const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    return { month: label, ...buckets[k] };
  });
  const series = [...top, ...(Object.values(buckets).some((b) => b.Other) ? ["Other"] : [])];
  return (
    <div className="mt-5" data-testid="industry-trend-chart">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Industry adoption over time</p>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
          <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <ReTooltip cursor={{ fill: "rgba(47,107,255,0.06)" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Bar key={s} dataKey={s} stackId="a" fill={TREND_COLORS[i % TREND_COLORS.length]} radius={i === series.length - 1 ? [4, 4, 0, 0] : 0} maxBarSize={44} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function OnboardingInsights({ users }) {
  const answered = users.filter((u) => u.role_profile || u.industry);
  if (!answered.length) return null;
  const count = (key) => {
    const m = {};
    answered.forEach((u) => { const v = u[key]; if (v) m[v] = (m[v] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const roles = count("role_profile");
  const industries = count("industry").slice(0, 4);
  const plans = count("plan_type").slice(0, 4);
  const autopilot = answered.filter((u) => u.autopilot_optin === true).length;
  const Chip = ({ label, n }) => (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#2f6bff]/8 text-[#2547a8] border border-[#2f6bff]/20">
      {label} <b className="text-[#2f6bff]">{n}</b>
    </span>
  );
  return (
    <div className="border border-border rounded-xl bg-card p-5 mb-8" data-testid="onboarding-insights">
      <div className="flex items-center gap-2 mb-3">
        <Music2 className="h-4 w-4 text-[#2f6bff]" />
        <h2 className="font-display font-extrabold">Onboarding answers</h2>
        <span className="text-xs text-muted-foreground">— what {answered.length} tester{answered.length === 1 ? "" : "s"} told Maestro</span>
        <span className="ml-auto text-xs font-bold text-emerald-600" data-testid="onboarding-autopilot-count">Auto Pilot opt-in: {autopilot}/{answered.length}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Roles</p>
          <div className="flex flex-wrap gap-1.5">{roles.length ? roles.map(([l, n]) => <Chip key={l} label={l} n={n} />) : <span className="text-xs text-muted-foreground">—</span>}</div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Industries</p>
          <div className="flex flex-wrap gap-1.5">{industries.length ? industries.map(([l, n]) => <Chip key={l} label={l} n={n} />) : <span className="text-xs text-muted-foreground">—</span>}</div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Plan types</p>
          <div className="flex flex-wrap gap-1.5">{plans.length ? plans.map(([l, n]) => <Chip key={l} label={l} n={n} />) : <span className="text-xs text-muted-foreground">—</span>}</div>
        </div>
      </div>
      <IndustryTrend answered={answered} />
    </div>
  );
}

function NdaCompliance({ users }) {
  const [open, setOpen] = useState(false);
  const signed = users.filter((u) => u.nda_signed);
  const unsigned = users.filter((u) => !u.nda_signed);
  const exportCsv = () => {
    const rows = [["Name", "Email", "Role", "NDA signed", "Signed at", "Signature", "Version"]];
    users.forEach((u) => rows.push([u.name, u.email, u.role, u.nda_signed ? "yes" : "no", u.nda_signed_at || "", u.nda_signature || "", u.nda_version || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "nda-compliance.csv";
    a.click();
  };
  return (
    <div className="border border-border rounded-xl p-5 mb-6" data-testid="nda-compliance-panel">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h2 className="font-display font-bold">NDA compliance</h2>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${unsigned.length === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="nda-compliance-count">
          {signed.length} / {users.length} signed
        </span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="gap-1.5" onClick={exportCsv} data-testid="nda-export-csv">Export CSV</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} data-testid="nda-compliance-toggle">{open ? "Hide details" : "View details"}</Button>
      </div>
      {unsigned.length > 0 && (
        <p className="text-xs text-amber-700 mt-2" data-testid="nda-unsigned-list">
          <b>Not yet signed:</b> {unsigned.map((u) => u.name).join(", ")} — they'll be prompted at their next sign-in.
        </p>
      )}
      {open && (
        <div className="overflow-x-auto mt-3 border border-border rounded-lg">
          <table className="w-full text-sm" data-testid="nda-compliance-table">
            <thead className="bg-secondary/50 text-left text-muted-foreground">
              <tr>{["User", "Email", "Signed", "Signature", "Version"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`nda-detail-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{u.name}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{u.email}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{u.nda_signed ? <span className="text-emerald-700 font-semibold">{fmtDate(u.nda_signed_at)}</span> : <span className="text-amber-600 font-semibold">not signed</span>}</td>
                  <td className="px-3 py-2 italic whitespace-nowrap">{u.nda_signature || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{u.nda_version || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
